import React, { useEffect, useMemo, useRef } from 'react';
import {
  Grid,
  GridColumn,
  GridItemChangeEvent,
  GridCellProps,
  GridSortChangeEvent
} from "@progress/kendo-react-grid";
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { plusIcon, trashIcon } from '@progress/kendo-svg-icons';
import { Gateways, GatewayStatus } from '../../../../Models/Assets/AssetTemplateReadDto';
import { enMessages } from '../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';

interface Props {
  gateways: Gateways[];
  onGatewaysChange: (gateways: Gateways[]) => void;
  onAddGateway?: () => void;
  instructionText?: string;
  /** Edit template: delete sets status=Deleted (soft). Create template: delete removes from list (hard). */
  isEditTemplate?: boolean;
}

/** Stable key for grid: id when present, otherwise temp key by gatewayId */
function getDataItemKey(item: Gateways): string {
  return item.id || `temp-gw-${item.gatewayId}`;
}

const INITIAL_SORT: SortDescriptor[] = [{ field: 'name', dir: 'asc' }];

function isGatewayActive(gw: Gateways): boolean {
  if (gw.status !== undefined) return gw.status === 'Active';
  return true;
}

function isGatewayNameDuplicate(newName: string, gateways: Gateways[], excludeKey: string): boolean {
  const normalized = (newName || '').trim().toLowerCase();
  if (!normalized) return false;
  return gateways.some(
    (gw) => isGatewayActive(gw) && getDataItemKey(gw) !== excludeKey && (gw.name || '').trim().toLowerCase() === normalized
  );
}

const GatewaysTableTemplateComponent: React.FC<Props> = ({
  gateways,
  onGatewaysChange,
  onAddGateway,
  instructionText,
  isEditTemplate = false
}) => {
  const localizationService = useLocalization();
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<SortDescriptor[]>(INITIAL_SORT);
  const [orderedRowKeys, setOrderedRowKeys] = React.useState<string[]>([]);
  const previousVisibleKeysSignatureRef = useRef<string>('');
  const previousSortSignatureRef = useRef<string>('');

  const activeGateways = useMemo(() => gateways.filter(isGatewayActive), [gateways]);
  const activeGatewayKeysSignature = useMemo(
    () => activeGateways.map(getDataItemKey).join('|'),
    [activeGateways]
  );

  useEffect(() => {
    const sortSignature = JSON.stringify(sort);
    const didSortChange = previousSortSignatureRef.current !== sortSignature;
    const didVisibleRowsChange = previousVisibleKeysSignatureRef.current !== activeGatewayKeysSignature;

    if (!didSortChange && !didVisibleRowsChange) {
      return;
    }

    const sortedGateways = sort.length > 0 ? orderBy(activeGateways, sort) : activeGateways;
    setOrderedRowKeys(sortedGateways.map(getDataItemKey));
    previousSortSignatureRef.current = sortSignature;
    previousVisibleKeysSignatureRef.current = activeGatewayKeysSignature;
  }, [activeGatewayKeysSignature, activeGateways, sort]);

  const editableGateways = useMemo(() => {
    const gatewaysByKey = new Map(activeGateways.map((gateway) => [getDataItemKey(gateway), gateway]));
    const orderedGateways = orderedRowKeys.length > 0
      ? orderedRowKeys
        .map((rowKey) => gatewaysByKey.get(rowKey))
        .filter((gateway): gateway is Gateways => gateway !== undefined)
      : (sort.length > 0 ? orderBy(activeGateways, sort) : activeGateways);

    return orderedGateways.map(gw => ({
      ...gw,
      inEdit: true,
      _gridKey: getDataItemKey(gw)
    }));
  }, [activeGateways, orderedRowKeys, sort]);

  const handleItemChange = (event: GridItemChangeEvent) => {
    const dataItem = event.dataItem as Gateways & { _gridKey?: string };
    const key = dataItem._gridKey || dataItem.id || `temp-gw-${dataItem.gatewayId}`;
    const newGateways = gateways.map((gw: Gateways) =>
      getDataItemKey(gw) === key ? { ...gw, [event.field!]: event.value } : gw
    );
    onGatewaysChange(newGateways);

    if (event.field === 'name') {
      const newName = (event.value as string) ?? '';
      if (!newName.trim()) {
        setNameError(localizationService.toLanguageString('custom.uniqueName', enMessages.custom.uniqueName));
      } else if (isGatewayNameDuplicate(newName, newGateways, key)) {
        setNameError(localizationService.toLanguageString('custom.nameExistsInTemplate', 'This name already exists in this template. Names must be unique.'));
      } else {
        setNameError(null);
      }
    }
  };

  const handleDeleteGateway = (dataItem: Gateways) => {
    const key = getDataItemKey(dataItem);
    if (isEditTemplate) {
      const updatedGateways = gateways.map(gw =>
        getDataItemKey(gw) === key ? { ...gw, status: 'Deleted' as GatewayStatus } : gw
      );
      onGatewaysChange(updatedGateways);
    } else {
      const updatedGateways = gateways.filter(gw => getDataItemKey(gw) !== key);
      onGatewaysChange(updatedGateways);
    }
  };

  const deleteCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td style={{ textAlign: 'center' }}>
        <Button
          type="button"
          themeColor="primary"
          svgIcon={trashIcon}
          title={localizationService.toLanguageString('custom.delete', enMessages.custom.delete)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDeleteGateway(dataItem as Gateways);
          }}
        />
      </td>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between align-middle mb-0">
        <div>
          <div className="text-xl font-bold">
            {localizationService.toLanguageString('custom.gateways', enMessages.custom.gateways)}
          </div>
          {instructionText && (
            <p className="text-xs text-gray-500 mt-1 px-1">{instructionText}</p>
          )}
          {nameError && (
            <p className="text-xs text-red-600 mt-1 px-1" style={{ marginBottom: 0 }}>* {nameError}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onAddGateway && (
            <Button
              type="button"
              themeColor="primary"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              svgIcon={plusIcon}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddGateway();
              }}
            />
          )}
        </div>
      </div>
      <div className="overflow-auto min-w-0 flex-shrink-0" style={{ height: 120 }}>
        <Grid
          data={editableGateways}
          style={{ height: '100%', minWidth: 280 }}
          scrollable="scrollable"
          dataItemKey="_gridKey"
          onItemChange={handleItemChange}
          sortable={true}
          sort={sort}
          onSortChange={(event: GridSortChangeEvent) => setSort(event.sort)}
          editable={true}
          editField="inEdit"
        >
          <GridColumn
            field="name"
            title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
          />
          {onAddGateway && (
            <GridColumn
              title={localizationService.toLanguageString('custom.actions', enMessages.custom.actions)}
              width="100px"
              sortable={false}
              filterable={false}
              editable={false}
              cells={{
                data: deleteCell
              }}
            />
          )}
        </Grid>
      </div>
    </div>
  );
};

export default GatewaysTableTemplateComponent;
